/** Printed scale interpretation is independent of trade, tag spelling and sheet templates. */
export type ScaleFinding = {
  printedText: string;
  scope: "page" | "multiple_views" | "unknown";
  evidence: string;
  sourceImageIds: string[];
  warnings: string[];
  dimension: { label: string; points: { x: number; y: number }[] } | null;
};
export type ScaleAssessment = {
  state:
    | "verified"
    | "applied"
    | "needs_confirmation"
    | "unresolved"
    | "reused";
  reason: string;
  printedText?: string;
  pixelsPerFoot?: number;
  pageAspectRatio?: number;
  relativeError?: number;
};

function normalized(text: string) {
  return text
    .replace(/[′’‘]/g, "'")
    .replace(/[″“”]/g, '"')
    .replace(/[–−]/g, "-")
    .trim();
}
function positiveNumber(text: string): number | null {
  const parts = text.trim().split(/\s+/);
  const values = parts.map((part) => {
    const fraction = part.split("/").map(Number);
    return fraction.length === 2 ? fraction[0] / fraction[1] : Number(part);
  });
  const value = values.reduce((sum, n) => sum + n, 0);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/** Explicit units are required; a bare dimension number never establishes feet. */
export function printedDimensionFeet(text: string): number | null {
  const label = normalized(text);
  const metric = /^(\d+(?:\.\d+)?)\s*(mm|cm|m)$/i.exec(label);
  if (metric)
    return (
      (Number(metric[1]) *
        ({ mm: 0.001, cm: 0.01, m: 1 }[metric[2].toLowerCase()] ?? 0)) /
      0.3048
    );
  const imperial =
    /^(\d+(?:\.\d+)?)\s*'\s*(?:-?\s*(\d+(?:\.\d+)?(?:\s+\d+\/\d+)?|\d+\/\d+)\s*")?$/.exec(
      label,
    );
  if (!imperial) return null;
  const inches = imperial[2]
    ? imperial[2] === "0"
      ? 0
      : positiveNumber(imperial[2])
    : 0;
  if (inches === null || inches >= 12) return null;
  const feet = Number(imperial[1]) + inches / 12;
  return feet > 0 ? feet : null;
}

export function printedInchesPerFoot(text: string): number | null {
  const label = normalized(text).replace(/^scale\s*:?\s*/i, "");
  const metric = /^1\s*:\s*(\d+(?:\.\d+)?)$/.exec(label);
  if (metric) return Number(metric[1]) > 0 ? 12 / Number(metric[1]) : null;
  const architectural =
    /^(\d+(?:\.\d+)?(?:\s+\d+\/\d+)?|\d+\/\d+)\s*"\s*=\s*(.+)$/.exec(label);
  if (!architectural) return null;
  const paper = positiveNumber(architectural[1]);
  const feet = printedDimensionFeet(architectural[2]);
  return paper && feet ? paper / feet : null;
}

/** Same width-normalized convention used by manual page-size/preset calibration. */
export function calibratePageScale(
  pageWidthInches: number,
  inchesPerFoot: number,
) {
  if (
    ![pageWidthInches, inchesPerFoot].every((n) => Number.isFinite(n) && n > 0)
  )
    throw new Error("Page size and drawing scale must be positive");
  const pixelsPerFoot = inchesPerFoot / pageWidthInches;
  return { pixelsPerFoot, feetPerPixel: 1 / pixelsPerFoot };
}

export function assessDrawingScale(
  finding: ScaleFinding,
  pageSizePoints: number[],
): ScaleAssessment {
  const unresolved = (reason: string): ScaleAssessment => ({
    state: "unresolved",
    reason,
    printedText: finding.printedText,
  });
  if (
    pageSizePoints.length !== 2 ||
    !pageSizePoints.every((n) => Number.isFinite(n) && n > 0)
  )
    return unresolved("The source page dimensions are unavailable");
  if (finding.scope !== "page" || finding.warnings.length)
    return unresolved(
      "The drawing has multiple view scales or unresolved scale context. Choose and confirm the scale for the measured view.",
    );
  const inchesPerFoot = printedInchesPerFoot(finding.printedText);
  if (
    !inchesPerFoot ||
    !finding.evidence.trim() ||
    !finding.sourceImageIds.length
  )
    return unresolved(
      "A legible, applicable printed scale was not established",
    );
  const { pixelsPerFoot } = calibratePageScale(
    pageSizePoints[0] / 72,
    inchesPerFoot,
  );
  const base = {
    printedText: finding.printedText,
    pixelsPerFoot,
    pageAspectRatio: pageSizePoints[1] / pageSizePoints[0],
  };
  const feet =
    finding.dimension && printedDimensionFeet(finding.dimension.label);
  if (!feet || !finding.dimension)
    return {
      ...base,
      state: "needs_confirmation",
      reason:
        "Printed scale found. Confirm it because no independent dimension could be checked.",
    };
  const points = finding.dimension.points;
  if (
    points.length !== 2 ||
    points.some(
      (p) => ![p.x, p.y].every((n) => Number.isFinite(n) && n >= 0 && n <= 1),
    )
  )
    return unresolved("The dimension check has invalid source coordinates");
  const distance =
    Math.hypot(
      points[1].x - points[0].x,
      (points[1].y - points[0].y) * base.pageAspectRatio,
    ) / pixelsPerFoot;
  const relativeError = Math.abs(distance - feet) / feet;
  if (relativeError > 0.05)
    return {
      ...base,
      relativeError,
      state: "unresolved",
      reason:
        "The printed scale disagrees with the dimension check. The PDF may have been resized; verify its scale.",
    };
  return {
    ...base,
    relativeError,
    state: "verified",
    reason: "Printed scale agrees with an independent dimension within 5%.",
  };
}
